import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';

// Production-safe API host: same-origin in deployed builds, env var in preview.
const _ENV_BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const _ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const _isPreviewEnv = /preview\.emergentagent\.com$/.test(
  (typeof window !== 'undefined' && window.location.hostname) || ''
);
const API_HOST = _isPreviewEnv ? _ENV_BACKEND : (_ORIGIN || _ENV_BACKEND);

import { 
  BookOpen, ArrowLeft, CheckCircle, XCircle, Award, 
  RefreshCw, Download, Mail, Clock, Lock,
  ChevronRight, AlertCircle, PlayCircle, FileText, ExternalLink, ShieldCheck
} from 'lucide-react';
import ClinicalAffiliationBanner from '../components/ClinicalAffiliationBanner';
import AITutorChat from '../components/AITutorChat';

const API_URL = API_HOST || '';
const PASSING_SCORE_DISPLAY = 70;

// Maps online course IDs to their downloadable training manual endpoints
const COURSE_MANUAL_ENDPOINTS = {
  'cna-prep': '/api/cna-training-manual-comprehensive/download',
  'phlebotomy-prep': '/api/phlebotomy-training-manual/download',
  'emt-prep': '/api/emt-training-manual/download',
  'homeland-security-prep': '/api/homeland-security-training-manual/download',
  'lpn-prep': '/api/lpn-training-manual/download',
};

// FEMA SID + ICS prerequisite info for Homeland Security apprentices.
const HOMELAND_SECURITY_FEMA_INFO = {
  sidUrl: 'https://cdp.dhs.gov/femasid',
  emiUrl: 'https://training.fema.gov/is/',
  icsCourses: [
    { code: 'IS-100.C', title: 'Introduction to the Incident Command System (ICS 100)', url: 'https://training.fema.gov/is/courseoverview.aspx?code=IS-100.c' },
    { code: 'IS-200.C', title: 'Basic Incident Command System for Initial Response (ICS 200)', url: 'https://training.fema.gov/is/courseoverview.aspx?code=IS-200.c' },
    { code: 'IS-700.B', title: 'Introduction to the National Incident Management System, NIMS (IS-700)', url: 'https://training.fema.gov/is/courseoverview.aspx?code=IS-700.b' },
    { code: 'IS-800.D', title: 'National Response Framework, An Introduction (IS-800)', url: 'https://training.fema.gov/is/courseoverview.aspx?code=IS-800.d' },
  ],
};

const OnlineCourseViewer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [examStatus, setExamStatus] = useState(null);
  const [showExam, setShowExam] = useState(null); // 'midterm' or 'final'
  const [examQuestions, setExamQuestions] = useState([]);
  const [examAnswers, setExamAnswers] = useState({});
  const [examResult, setExamResult] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/login?redirect=/online-course/' + courseId);
      return;
    }
    setUser(JSON.parse(userData));
  }, [courseId, navigate]);

  useEffect(() => {
    if (user) {
      fetchCourseModules();
      fetchExamStatus();
    }
  }, [user, courseId]);

  const fetchExamStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/exam-status/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setExamStatus(data);
      }
    } catch (error) {
      console.error('Error fetching exam status:', error);
    }
  };

  const fetchCourseModules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/modules/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setCourseData(data);

        // Deep-link support: ?module=N picks that exact module on entry.
        const requested = parseInt(searchParams.get('module'), 10);
        let target = null;
        if (!Number.isNaN(requested)) {
          target = data.modules.find(m => m.module_number === requested);
        }
        if (!target) {
          // Fall back to first incomplete, otherwise first module.
          target = data.modules.find(m => !m.quiz_passed) || data.modules[0];
        }
        setSelectedModule(target);

        // Welcome modal on first arrival after a successful enrollment payment.
        // Triggered by ?welcome=1 OR ?session_id=… (Stripe success redirect).
        const isFreshPayment = searchParams.get('welcome') === '1' || searchParams.get('session_id');
        const seenKey = `welcome-seen-${courseId}-${user.id}`;
        const alreadyStarted = data.modules.some(m => m.quiz_passed) || data.modules_completed > 0;
        if ((isFreshPayment || !alreadyStarted) && !sessionStorage.getItem(seenKey)) {
          setShowWelcome(true);
          sessionStorage.setItem(seenKey, '1');
        }
      } else {
        navigate('/online-courses');
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };



  const fetchQuiz = async () => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/module/${selectedModule.module_number}/quiz/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        setQuizQuestions(data);
        setSelectedAnswers({});
        setQuizResult(null);
        setShowQuiz(true);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/module/${selectedModule.module_number}/quiz/${user.id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: selectedAnswers })
      });

      if (response.ok) {
        const result = await response.json();
        setQuizResult(result);
        // Refresh course data
        fetchCourseModules();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const retakeQuiz = () => {
    setQuizResult(null);
    setSelectedAnswers({});
    fetchQuiz();
  };

  const selectModule = (module) => {
    setSelectedModule(module);
    setShowQuiz(false);
    setQuizResult(null);
    setQuizQuestions(null);
  };

  const downloadCertificate = () => {
    if (examStatus && examStatus.admin_approved) {
      window.open(`${API_URL}/api/online-courses/${courseId}/certificate/${user.id}`, '_blank');
    } else {
      alert('Certificate requires admin approval. All exams must be passed first.');
    }
  };

  const handleExamPayment = async (examType) => {
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/exam/${examType}/mark-paid/${user.id}`, {
        method: 'POST'
      });
      if (response.ok) {
        fetchExamStatus();
        setShowExam(examType);
        loadExamQuestions(examType);
      }
    } catch (error) {
      alert('Error processing exam payment');
    }
  };

  const loadExamQuestions = (examType) => {
    if (!courseData) return;
    const modules = courseData.modules;
    let relevantModules;
    if (examType === 'midterm') {
      relevantModules = modules.slice(0, Math.floor(modules.length / 2));
    } else {
      relevantModules = modules;
    }
    // Namespace each quiz question ID with its module number. Module quiz IDs
    // ('q1'..'q5') collide across modules, so for midterm/final aggregation
    // every question needs a globally unique id. Backend submit_exam uses the
    // same "m{N}_q{origId}" format when grading.
    const questions = [];
    relevantModules.forEach((mod) => {
      if (mod.quiz) {
        mod.quiz.forEach((q) =>
          questions.push({ ...q, id: `m${mod.module_number}_${q.id}` }),
        );
      }
    });
    setExamQuestions(questions);
    setExamAnswers({});
    setExamResult(null);
  };

  const submitExam = async (examType) => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/online-courses/${courseId}/exam/${examType}/submit/${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: examAnswers })
      });
      if (response.ok) {
        const result = await response.json();
        setExamResult(result);
        fetchExamStatus();
      } else {
        const err = await response.json();
        alert(err.detail || 'Error submitting exam');
      }
    } catch (error) {
      alert('Error submitting exam');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={48} style={{ animation: 'spin 1s linear infinite', color: '#1e40af' }} />
          <p style={{ marginTop: '16px', color: '#64748b' }}>Loading course...</p>
        </div>
      </div>
    );
  }

  if (!courseData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <AlertCircle size={48} color="#dc2626" style={{ marginBottom: '16px' }} />
          <h2 style={{ color: '#dc2626' }}>Course not found or not enrolled</h2>
          <Link to="/online-courses" style={{ color: '#1e40af' }}>Back to courses</Link>
        </div>
      </div>
    );
  }

  const allModulesComplete = courseData.modules_completed === courseData.total_modules;
  const halfModulesComplete = courseData.modules_completed >= Math.floor(courseData.total_modules / 2);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Welcome modal — celebrates a fresh enrollment and orients the student */}
      {showWelcome && (
        <div
          data-testid="course-welcome-modal"
          onClick={() => setShowWelcome(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.72)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'linear-gradient(140deg, #16a34a 0%, #15803d 60%, #166534 100%)',
              color: 'white', borderRadius: 18, padding: '32px 36px', maxWidth: 520, width: '100%',
              boxShadow: '0 24px 60px -16px rgba(0,0,0,0.6)', textAlign: 'center',
            }}
          >
            <div style={{ width: 64, height: 64, margin: '0 auto 12px', borderRadius: '50%', background: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Award size={36} color="#fde68a" />
            </div>
            <div style={{ fontSize: '0.78rem', letterSpacing: 1.4, textTransform: 'uppercase', opacity: 0.92 }}>You&apos;re officially enrolled</div>
            <h2 style={{ margin: '8px 0 0', fontSize: '1.6rem', fontWeight: 800 }}>Welcome to {courseData.course_title}</h2>
            <p style={{ marginTop: 14, fontSize: '0.95rem', opacity: 0.94, lineHeight: 1.55 }}>
              Apprentice ID <strong style={{ color: '#fde68a' }}>{courseData.apprentice_id || '—'}</strong>
              {' · '}{courseData.total_modules} modules waiting for you. Take a screenshot of this ID — you&apos;ll see it on your final certificate.
            </p>
            <button
              type="button"
              onClick={() => setShowWelcome(false)}
              data-testid="welcome-start-now-btn"
              style={{
                marginTop: 22, padding: '12px 28px', borderRadius: 999, border: 0,
                background: '#fde68a', color: '#14532d', fontWeight: 800, fontSize: '1rem',
                cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                boxShadow: '0 8px 18px -6px rgba(0,0,0,0.35)',
              }}
            >
              Start Module {selectedModule?.module_number || 1} now <ChevronRight size={18} />
            </button>
            <div style={{ marginTop: 14, fontSize: '0.78rem', opacity: 0.8 }}>
              Tip: every module is a quick read + 5-question quiz. Aim for 1 module a day.
            </div>
          </div>
        </div>
      )}

      {/* Persistent progress bar — visible on every module page so the student
          always knows where they are inside the course. */}
      <div data-testid="persistent-progress-strip" style={{ background: '#0f172a', color: '#cbd5e1', padding: '8px 16px', fontSize: '0.78rem' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#f1f5f9' }}>
            {selectedModule
              ? `Module ${selectedModule.module_number} of ${courseData.total_modules}`
              : `Modules: ${courseData.modules_completed} / ${courseData.total_modules}`}
          </span>
          <span style={{ opacity: 0.6 }}>·</span>
          <span>{Math.round((courseData.modules_completed / Math.max(1, courseData.total_modules)) * 100)}% complete</span>
          <div style={{ flex: 1, minWidth: 160, height: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 999, overflow: 'hidden', marginLeft: 8 }}>
            <div
              style={{
                height: '100%',
                width: `${Math.round((courseData.modules_completed / Math.max(1, courseData.total_modules)) * 100)}%`,
                background: 'linear-gradient(90deg, #34d399, #fde68a)',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
          {(() => {
            const cur = selectedModule?.module_number;
            const next = courseData.modules.find(m => m.module_number > (cur || 0));
            if (!next) return null;
            return (
              <span style={{ opacity: 0.85 }}>Next: <strong style={{ color: '#fde68a' }}>{next.title}</strong></span>
            );
          })()}
        </div>
      </div>

      {/* Header */}
      <header style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%)', color: 'white', padding: '16px 24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Link to="/online-courses" style={{ color: 'rgba(255,255,255,0.8)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', marginBottom: '4px' }}>
              <ArrowLeft size={16} /> Back to Courses
            </Link>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{courseData.course_title}</h1>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ background: 'rgba(255,255,255,0.2)', padding: '6px 12px', borderRadius: '20px', fontSize: '0.875rem' }}>
                {courseData.modules_completed}/{courseData.total_modules} Modules Complete
              </span>
              {COURSE_MANUAL_ENDPOINTS[courseId] && (
                <a
                  href={`${API_URL}${COURSE_MANUAL_ENDPOINTS[courseId]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-training-manual-btn"
                  style={{
                    background: '#0ea5e9',
                    color: 'white',
                    textDecoration: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <Download size={16} /> Training Manual
                </a>
              )}
              {allModulesComplete && (
                <a
                  href={`${API_URL}/api/online-courses/${courseId}/transcript/${user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-transcript-btn"
                  style={{
                    background: '#7c3aed',
                    color: 'white',
                    textDecoration: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '0.875rem'
                  }}
                >
                  <FileText size={16} /> Transcript
                </a>
              )}
              {allModulesComplete && (
                <button
                  onClick={downloadCertificate}
                  data-testid="download-cert-header"
                  style={{
                    background: '#22c55e',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Award size={18} /> Get Certificate
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* FEMA SID + ICS Prerequisites banner (Homeland Security only) */}
      {courseId === 'homeland-security-prep' && (
        <div
          data-testid="fema-sid-banner"
          style={{
            maxWidth: 1400,
            margin: '16px auto 0',
            padding: '20px 24px',
            background: 'linear-gradient(135deg, #0b2545 0%, #13315c 100%)',
            borderRadius: 12,
            border: '2px solid #c9a227',
            color: '#f8fafc',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <ShieldCheck size={20} color="#f7d774" />
            <strong style={{ letterSpacing: 0.6, color: '#f7d774', textTransform: 'uppercase', fontSize: '0.78rem' }}>
              FEMA SID Registration &amp; ICS Prerequisites — Required
            </strong>
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '0.88rem', lineHeight: 1.55 }}>
            Every Homeland Security apprentice must complete FEMA's free independent-study
            ICS/NIMS courses through the Emergency Management Institute (EMI) in addition to the
            eight online modules below. Start by registering for your free
            <strong> FEMA Student ID (SID)</strong>, then enroll in the four required courses and
            email the completion certificates (PDF) to <em>medcare.nursing@yahoo.com</em> with
            your Apprentice ID in the subject line.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <a
              href={HOMELAND_SECURITY_FEMA_INFO.sidUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fema-sid-register-link"
              style={{
                background: '#f7d774',
                color: '#0b2545',
                padding: '8px 14px',
                borderRadius: 8,
                fontWeight: 700,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.85rem',
              }}
            >
              <ExternalLink size={14} /> Register your FEMA SID
            </a>
            <a
              href={HOMELAND_SECURITY_FEMA_INFO.emiUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fema-emi-link"
              style={{
                background: 'rgba(255,255,255,0.12)',
                color: '#fff',
                padding: '8px 14px',
                borderRadius: 8,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.85rem',
                border: '1px solid rgba(247,215,116,0.55)',
              }}
            >
              <ExternalLink size={14} /> EMI Course Catalog
            </a>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 10,
            }}
          >
            {HOMELAND_SECURITY_FEMA_INFO.icsCourses.map((ics) => (
              <a
                key={ics.code}
                href={ics.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ics-${ics.code.toLowerCase()}-link`}
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(247,215,116,0.35)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  color: '#e2e8f0',
                  textDecoration: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <span style={{ color: '#f7d774', fontWeight: 700, fontSize: '0.78rem' }}>
                  {ics.code}
                </span>
                <span style={{ fontSize: '0.82rem', lineHeight: 1.35 }}>{ics.title}</span>
              </a>
            ))}
          </div>
          <div
            style={{
              marginTop: 12,
              fontSize: '0.75rem',
              color: '#bfdbfe',
              fontStyle: 'italic',
            }}
          >
            How to submit: log in to FEMA's Student Portal with your SID, take each course
            online, download the completion certificate, and email all four to MedCare.
            Admin will mark your FEMA Prerequisites as verified.
          </div>
        </div>
      )}

      {/* Clinical Affiliation Form banner — all hybrid medical programs */}
      {['lpn-prep', 'cna-prep', 'phlebotomy-prep', 'emt-prep', 'medical-aide-prep', 'medical-terminology-prep'].includes(courseId) && (
        <ClinicalAffiliationBanner user={user} apiBaseUrl={API_URL} courseId={courseId} />
      )}

      {/* Main Content */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', minHeight: 'calc(100vh - 80px)' }}>
        {/* Sidebar - Module List */}
        <div style={{ background: 'white', borderRight: '1px solid #e5e7eb', overflowY: 'auto' }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
            <p style={{ margin: '0 0 4px', fontSize: '0.875rem', color: '#64748b' }}>Apprentice ID</p>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#1e3a5f' }}>{courseData.apprentice_id}</p>
          </div>
          
          <div style={{ padding: '8px' }}>
            {courseData.modules.map((module, idx) => {
              const isSelected = selectedModule?.module_number === module.module_number;
              const isComplete = module.quiz_passed;
              
              return (
                <button
                  key={module.module_number}
                  onClick={() => selectModule(module)}
                  data-testid={`module-btn-${module.module_number}`}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    marginBottom: '4px',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    border: isSelected ? '2px solid #3b82f6' : '2px solid transparent',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: isComplete ? '#22c55e' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {isComplete ? (
                        <CheckCircle size={18} color="white" />
                      ) : (
                        <span style={{ fontWeight: 'bold', color: '#64748b', fontSize: '0.875rem' }}>{module.module_number}</span>
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ 
                        margin: 0, 
                        fontWeight: isSelected ? 'bold' : 'normal',
                        color: '#1e3a5f',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {module.title}
                      </p>
                      <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                        {isComplete ? `Passed: ${(module.quiz_score || 0).toFixed(0)}%` :
                         `${module.quiz_question_count || 5} questions · ${PASSING_SCORE_DISPLAY}% to pass`}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{ overflowY: 'auto', padding: '24px' }}>
          {selectedModule && !showQuiz && (
            <>
              {/* Module Header */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ 
                    background: selectedModule.quiz_passed ? '#dcfce7' : '#dbeafe',
                    color: selectedModule.quiz_passed ? '#166534' : '#1e40af',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    Module {selectedModule.module_number}
                  </span>
                  {selectedModule.quiz_passed && (
                    <span style={{ color: '#22c55e', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.875rem' }}>
                      <CheckCircle size={16} /> Completed
                    </span>
                  )}
                </div>
                <h2 style={{ margin: '0 0 8px', color: '#1e3a5f' }}>{selectedModule.title}</h2>
                <p style={{ margin: 0, color: '#64748b' }}>{selectedModule.description}</p>
              </div>

              {/* Two-step process: Read → Quiz */}
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                padding: '18px 22px',
                borderRadius: '12px',
                marginBottom: '22px',
                border: '1px solid #bfdbfe',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}>
                <div style={{ flex: '1 1 240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>1</div>
                    <span style={{ fontWeight: 700, color: '#1e3a5f' }}>Read the training manual</span>
                  </div>
                  <p style={{ margin: '6px 0 0 34px', color: '#475569', fontSize: '0.85rem' }}>
                    The full course manual is open in the panel to the right — search, zoom, and reference it freely while you test.
                  </p>
                </div>
                <div style={{ flex: '1 1 240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: selectedModule.quiz_passed ? '#22c55e' : '#f59e0b', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>
                      {selectedModule.quiz_passed ? <CheckCircle size={14} /> : '2'}
                    </div>
                    <span style={{ fontWeight: 700, color: '#1e3a5f' }}>Pass the module quiz ({PASSING_SCORE_DISPLAY}%+)</span>
                  </div>
                  <p style={{ margin: '6px 0 0 34px', color: '#475569', fontSize: '0.85rem' }}>
                    {selectedModule.quiz_passed
                      ? `Passed with ${(selectedModule.quiz_score || 0).toFixed(0)}%. You can review anytime.`
                      : `${selectedModule.quiz_question_count || 5} questions · unlimited attempts.`}
                  </p>
                </div>
              </div>

              {/* Reading + Manual PDF + Quiz */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                gap: '18px',
                marginBottom: '24px',
              }}
                data-testid="module-reading-and-manual"
              >
                {/* Left: Reading brief + Start/Retake Quiz */}
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', minHeight: '520px', display: 'flex', flexDirection: 'column' }}>
                  <h3 style={{ margin: '0 0 12px', color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BookOpen size={20} /> Module Reading Brief
                  </h3>
                  <p style={{ margin: 0, color: '#334155', fontSize: '0.92rem', lineHeight: 1.65, whiteSpace: 'pre-line' }} data-testid="reading-brief">
                    {selectedModule.reading_brief || selectedModule.description}
                  </p>

                  {selectedModule.objectives && selectedModule.objectives.length > 0 && (
                    <div style={{ marginTop: '16px', background: '#f8fafc', padding: '14px', borderRadius: '8px' }}>
                      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.6, color: '#0b2545', fontWeight: 700, marginBottom: '8px' }}>
                        Learning Objectives
                      </div>
                      <ul style={{ margin: 0, paddingLeft: '18px', color: '#475569', fontSize: '0.86rem', lineHeight: 1.6 }}>
                        {selectedModule.objectives.map((o, i) => (<li key={i}>{o}</li>))}
                      </ul>
                    </div>
                  )}

                  <div style={{ flex: 1 }} />

                  <div style={{ marginTop: '18px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                    {selectedModule.quiz_passed ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ background: '#dcfce7', padding: '10px 14px', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#166534', fontWeight: 700 }}>
                          <CheckCircle size={18} /> Passed: {(selectedModule.quiz_score || 0).toFixed(0)}%
                        </div>
                        <button
                          onClick={fetchQuiz}
                          data-testid="review-quiz-btn"
                          style={{ padding: '10px 18px', background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
                        >
                          Review Quiz
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={fetchQuiz}
                        data-testid="take-quiz-btn"
                        style={{ width: '100%', padding: '14px 20px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                      >
                        <PlayCircle size={20} />
                        {selectedModule.quiz_attempts > 0
                          ? `Retake Quiz (Best: ${(selectedModule.quiz_score || 0).toFixed(0)}%)`
                          : 'Start Module Quiz'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Right: Training Manual PDF */}
                <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', minHeight: '520px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, color: '#1e3a5f', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1.05rem' }}>
                      <FileText size={18} /> {courseData.course_title} — Training Manual
                    </h3>
                    {(selectedModule.manual_url || courseData.manual_url) && (
                      <a
                        href={`${API_URL}${selectedModule.manual_url || courseData.manual_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="open-manual-new-tab"
                        style={{ color: '#2563eb', fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none' }}
                      >
                        Open full tab <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  {(selectedModule.manual_url || courseData.manual_url) ? (
                    <iframe
                      src={`${API_URL}${selectedModule.manual_url || courseData.manual_url}#toolbar=1&navpanes=0`}
                      title="Training Manual"
                      data-testid="manual-pdf-iframe"
                      style={{ flex: 1, width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', minHeight: '460px' }}
                    />
                  ) : (
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.88rem' }}>
                      No manual linked for this course.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Quiz View */}
          {showQuiz && quizQuestions && !quizResult && (
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: '24px',
                marginBottom: '24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <div>
                    <h2 style={{ margin: '0 0 4px', color: '#1e3a5f' }}>Module {selectedModule.module_number} Quiz</h2>
                    <p style={{ margin: 0, color: '#64748b' }}>{selectedModule.title}</p>
                  </div>
                  <button
                    onClick={() => setShowQuiz(false)}
                    data-testid="back-to-module-btn"
                    style={{ background: '#f1f5f9', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    Back to Module
                  </button>
                </div>

                <div style={{ 
                  background: '#eff6ff', 
                  padding: '12px 16px', 
                  borderRadius: '8px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <span style={{ color: '#1e40af' }}>{quizQuestions.total_questions} Questions</span>
                  <span style={{ color: '#1e40af', fontWeight: 'bold' }}>{quizQuestions.passing_score}% to Pass</span>
                </div>

                {quizQuestions.questions.map((q, idx) => (
                  <div key={q.id} style={{ marginBottom: '24px', paddingBottom: '24px', borderBottom: idx < quizQuestions.questions.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <p style={{ fontWeight: 'bold', color: '#1e3a5f', marginBottom: '12px' }}>
                      {idx + 1}. {q.question}
                    </p>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {q.options.map((option, optIdx) => (
                        <label
                          key={optIdx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            background: selectedAnswers[q.id] === option ? '#dbeafe' : '#f8fafc',
                            border: `2px solid ${selectedAnswers[q.id] === option ? '#3b82f6' : '#e5e7eb'}`,
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            checked={selectedAnswers[q.id] === option}
                            onChange={() => setSelectedAnswers(prev => ({...prev, [q.id]: option}))}
                            style={{ width: '18px', height: '18px' }}
                          />
                          <span style={{ color: '#334155' }}>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}

                <button
                  onClick={submitQuiz}
                  disabled={submitting || Object.keys(selectedAnswers).length < quizQuestions.total_questions}
                  data-testid="submit-quiz-btn"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: Object.keys(selectedAnswers).length === quizQuestions.total_questions ? '#22c55e' : '#94a3b8',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: Object.keys(selectedAnswers).length === quizQuestions.total_questions ? 'pointer' : 'not-allowed',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  }}
                >
                  {submitting ? 'Submitting...' : `Submit Quiz (${Object.keys(selectedAnswers).length}/${quizQuestions.total_questions} answered)`}
                </button>
              </div>
            </div>
          )}

          {/* Quiz Results */}
          {quizResult && (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ 
                background: 'white', 
                borderRadius: '16px', 
                padding: '32px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: quizResult.passed ? '#dcfce7' : '#fef2f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  {quizResult.passed ? (
                    <CheckCircle size={50} color="#16a34a" />
                  ) : (
                    <XCircle size={50} color="#dc2626" />
                  )}
                </div>

                <h2 style={{ color: quizResult.passed ? '#166534' : '#991b1b', marginBottom: '8px' }}>
                  {quizResult.passed ? 'Module Complete!' : 'Not Quite Yet'}
                </h2>

                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: quizResult.passed ? '#16a34a' : '#dc2626', marginBottom: '8px' }}>
                  {quizResult.score.toFixed(0)}%
                </div>

                <p style={{ color: '#64748b', marginBottom: '24px' }}>
                  {quizResult.correct_count}/{quizResult.total_questions} correct • {quizResult.passing_score}% needed
                </p>

                {quizResult.course_complete && (
                  <div style={{ 
                    background: '#fef3c7', 
                    padding: '16px', 
                    borderRadius: '8px',
                    marginBottom: '24px'
                  }}>
                    <Award size={32} color="#d97706" style={{ marginBottom: '8px' }} />
                    <h3 style={{ color: '#92400e', margin: '0 0 8px' }}>Course Complete!</h3>
                    <p style={{ color: '#a16207', margin: '0 0 12px', fontSize: '0.875rem' }}>
                      You've completed all {quizResult.total_modules} modules!
                    </p>
                    <button
                      onClick={downloadCertificate}
                      data-testid="download-certificate-btn"
                      style={{
                        padding: '12px 24px',
                        background: '#22c55e',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <Download size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                      Download Certificate
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  {quizResult.passed ? (
                    <>
                      <button
                        onClick={() => {
                          setShowQuiz(false);
                          setQuizResult(null);
                          // Move to next module
                          const nextModule = courseData.modules.find(m => m.module_number === selectedModule.module_number + 1);
                          if (nextModule) {
                            selectModule(nextModule);
                          }
                        }}
                        style={{
                          padding: '12px 24px',
                          background: '#1e40af',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        {selectedModule.module_number < courseData.total_modules ? 'Next Module' : 'Back to Modules'}
                        <ChevronRight size={18} style={{ verticalAlign: 'middle', marginLeft: '4px' }} />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={retakeQuiz}
                      data-testid="retake-quiz-btn"
                      style={{
                        padding: '12px 24px',
                        background: '#1e40af',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      <RefreshCw size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                      Try Again
                    </button>
                  )}
                </div>

                {/* Question Review */}
                <div style={{ marginTop: '32px', textAlign: 'left' }}>
                  <h4 style={{ color: '#1e3a5f', marginBottom: '16px' }}>Question Review</h4>
                  {quizResult.results.map((r, idx) => (
                    <div 
                      key={idx}
                      style={{
                        padding: '12px',
                        marginBottom: '8px',
                        borderRadius: '8px',
                        background: r.is_correct ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${r.is_correct ? '#bbf7d0' : '#fecaca'}`
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                        {r.is_correct ? <CheckCircle size={18} color="#16a34a" /> : <XCircle size={18} color="#dc2626" />}
                        <div>
                          <p style={{ margin: '0 0 4px', fontWeight: '500', color: '#1e3a5f', fontSize: '0.875rem' }}>{r.question}</p>
                          <p style={{ margin: 0, fontSize: '0.8rem', color: r.is_correct ? '#166534' : '#991b1b' }}>
                            Your answer: {r.user_answer || 'No answer'}
                          </p>
                          {!r.is_correct && (
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#166534' }}>
                              Correct: {r.correct_answer}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Exams & Certification Section */}
      {examStatus && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
          {/* USDOL Registered Apprenticeship banner */}
          <div
            data-testid="apprenticeship-disclaimer"
            style={{
              background: '#ecfdf5',
              border: '1px solid #6ee7b7',
              borderRadius: '10px',
              padding: '12px 16px',
              marginBottom: '16px',
              fontSize: '0.82rem',
              color: '#065f46',
              lineHeight: 1.5,
            }}
          >
            <strong>USDOL Registered Apprenticeship:</strong> Delivered under the U.S.
            Department of Labor Registered Apprenticeship framework by MedCare Nursing
            Institute &amp; Training Academy Inc. through the Office of Workforce Development.
          </div>

          <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '2px solid #e2e8f0' }}>
            <h2 style={{ margin: '0 0 8px', color: '#1e3a5f', fontSize: '1.3rem' }}>
              {examStatus.requires_paid_exams ? 'Exams & Certification' : 'Course Completion & Certificate'}
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 20px' }}>
              {examStatus.requires_paid_exams
                ? 'Complete all module quizzes, then pass the Midterm ($149) and Final Exam ($149) to earn your certificate.'
                : 'Complete every module and its end-of-module quiz. Your certificate is awarded after admin approval — no additional exam fees.'}
              {examStatus.clinical_hours > 0 && ` Healthcare students must also complete ${examStatus.clinical_hours} clinical hours at assigned site.`}
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              {/* Midterm — only for courses that require paid exams (GED) */}
              {examStatus.requires_paid_exams && (
                <div style={{ padding: '20px', borderRadius: '10px', border: '2px solid', borderColor: examStatus.midterm.passed ? '#22c55e' : halfModulesComplete ? '#f59e0b' : '#e2e8f0', background: examStatus.midterm.passed ? '#f0fdf4' : '#fff' }}>
                  <h3 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                    {examStatus.midterm.passed ? <CheckCircle size={20} color="#22c55e" /> : <Clock size={20} color="#f59e0b" />}
                    Midterm Exam
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 12px' }}>
                    Covers first half of modules | Fee: $149
                  </p>
                  {examStatus.midterm.passed ? (
                    <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>
                      Passed ({examStatus.midterm.score}%)
                    </span>
                  ) : !halfModulesComplete ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Complete first {Math.floor(courseData.total_modules / 2)} modules to unlock</span>
                  ) : !examStatus.midterm.paid ? (
                    <button onClick={() => handleExamPayment('midterm')} data-testid="midterm-pay-btn"
                      style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                      Pay $149 &amp; Take Midterm
                    </button>
                  ) : (
                    <button onClick={() => { setShowExam('midterm'); loadExamQuestions('midterm'); }} data-testid="midterm-take-btn"
                      style={{ background: '#1e40af', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                      Take Midterm Exam
                    </button>
                  )}
                </div>
              )}

              {/* Final Exam — only for courses that require paid exams (GED) */}
              {examStatus.requires_paid_exams && (
                <div style={{ padding: '20px', borderRadius: '10px', border: '2px solid', borderColor: examStatus.final_exam.passed ? '#22c55e' : examStatus.final_exam.eligible ? '#7c3aed' : '#e2e8f0', background: examStatus.final_exam.passed ? '#f0fdf4' : '#fff' }}>
                  <h3 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                    {examStatus.final_exam.passed ? <CheckCircle size={20} color="#22c55e" /> : <Lock size={20} color="#7c3aed" />}
                    Final Exam
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 12px' }}>
                    Covers all modules | Fee: $149
                  </p>
                  {examStatus.final_exam.passed ? (
                    <span style={{ background: '#dcfce7', color: '#166534', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>
                      Passed ({examStatus.final_exam.score}%)
                    </span>
                  ) : !examStatus.final_exam.eligible ? (
                    <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Complete all modules &amp; pass midterm to unlock</span>
                  ) : !examStatus.final_exam.paid ? (
                    <button onClick={() => handleExamPayment('final')} data-testid="final-pay-btn"
                      style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                      Pay $149 &amp; Take Final
                    </button>
                  ) : (
                    <button onClick={() => { setShowExam('final'); loadExamQuestions('final'); }} data-testid="final-take-btn"
                      style={{ background: '#7c3aed', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                      Take Final Exam
                    </button>
                  )}
                </div>
              )}

              {/* Certificate Status */}
              <div style={{ padding: '20px', borderRadius: '10px', border: '2px solid', borderColor: examStatus.admin_approved ? '#22c55e' : '#e2e8f0', background: examStatus.admin_approved ? '#f0fdf4' : '#fff' }}>
                <h3 style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '1rem' }}>
                  <Award size={20} color={examStatus.admin_approved ? '#22c55e' : '#9ca3af'} />
                  Certificate
                </h3>
                <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0 0 12px' }}>
                  Awarded after admin approval
                </p>
                {examStatus.admin_approved ? (
                  <button onClick={downloadCertificate} data-testid="download-certificate-btn"
                    style={{ background: '#22c55e', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem' }}>
                    Download Certificate
                  </button>
                ) : examStatus.certificate_eligible ? (
                  <span style={{ background: '#fef3c7', color: '#92400e', padding: '4px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600' }}>
                    Awaiting Admin Approval
                  </span>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>
                    {examStatus.requires_paid_exams ? 'Pass all exams to qualify' : 'Complete all modules to qualify'}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exam Modal */}
      {showExam && examQuestions.length > 0 && !examResult && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '32px', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 8px', color: '#1e3a5f' }}>{showExam === 'midterm' ? 'Midterm Exam' : 'Final Exam'}</h2>
            <p style={{ color: '#64748b', margin: '0 0 20px', fontSize: '0.875rem' }}>
              Answer all questions. You need 70% to pass. | {examQuestions.length} questions
            </p>
            {examQuestions.map((q, idx) => (
              <div key={q.id} style={{ marginBottom: '20px', padding: '16px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <p style={{ fontWeight: '600', margin: '0 0 10px', color: '#1e3a5f', fontSize: '0.9rem' }}>
                  {idx + 1}. {q.question}
                </p>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {q.options.map((opt, oi) => (
                    <label key={oi} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', borderRadius: '6px', border: examAnswers[q.id] === opt ? '2px solid #1e40af' : '1px solid #e2e8f0', background: examAnswers[q.id] === opt ? '#eff6ff' : 'white', cursor: 'pointer' }}>
                      <input type="radio" name={q.id} value={opt} checked={examAnswers[q.id] === opt}
                        onChange={() => setExamAnswers({ ...examAnswers, [q.id]: opt })} />
                      <span style={{ fontSize: '0.85rem' }}>{opt}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={() => { setShowExam(null); setExamQuestions([]); }}
                style={{ padding: '12px 24px', background: '#f1f5f9', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => submitExam(showExam)} disabled={submitting} data-testid="submit-exam-btn"
                style={{ padding: '12px 24px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', flex: 1 }}>
                {submitting ? 'Submitting...' : `Submit ${showExam === 'midterm' ? 'Midterm' : 'Final Exam'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exam Result Modal */}
      {examResult && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '40px', maxWidth: '480px', width: '100%', textAlign: 'center' }}>
            {examResult.passed ? <CheckCircle size={64} color="#22c55e" /> : <XCircle size={64} color="#dc2626" />}
            <h2 style={{ margin: '16px 0 8px', color: examResult.passed ? '#166534' : '#991b1b' }}>
              {examResult.passed ? 'Congratulations!' : 'Not Passed'}
            </h2>
            <p style={{ fontSize: '2rem', fontWeight: '700', color: '#1e3a5f', margin: '8px 0' }}>{examResult.score}%</p>
            <p style={{ color: '#64748b', margin: '0 0 24px' }}>
              {examResult.correct_count}/{examResult.total_questions} correct | Passing: {examResult.passing_score}%
            </p>
            <p style={{ fontSize: '0.9rem', color: '#374151', margin: '0 0 24px' }}>{examResult.message}</p>
            <button onClick={() => { setExamResult(null); setShowExam(null); setExamQuestions([]); }}
              style={{ padding: '12px 32px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
              Close
            </button>
          </div>
        </div>
      )}
      <AITutorChat
        userId={user?.id}
        context={{
          course_id: courseData?.id,
          course_title: courseData?.title,
          module_number: selectedModule?.module_number,
          module_title: selectedModule?.title,
        }}
      />
    </div>
  );
};

export default OnlineCourseViewer;
